import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosActaAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    FileText,
    Plus,
    Search,
    ArrowLeft,
    Calendar,
    Clock,
    MapPin,
    Users,
    Eye,
    CheckCircle,
    Filter,
} from 'lucide-react';

const TIPOS_ACTA = {
    entrenamiento: { label: 'Entrenamiento', color: 'bg-blue-100 text-blue-700' },
    guardia: { label: 'Guardia', color: 'bg-green-100 text-green-700' },
    academia: { label: 'Academia', color: 'bg-purple-100 text-purple-700' },
    emergencia: { label: 'Emergencia', color: 'bg-red-100 text-red-700' },
    reunion: { label: 'Reunión', color: 'bg-yellow-100 text-yellow-700' },
    inspeccion: { label: 'Inspección', color: 'bg-orange-100 text-orange-700' },
    otro: { label: 'Otro', color: 'bg-gray-100 text-gray-700' },
};

export default function BomberosActasPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [actas, setActas] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filteredActas, setFilteredActas] = useState([]);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterTipo, setFilterTipo] = useState('all');
    const [filterMes, setFilterMes] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterActas();
    }, [actas, searchTerm, filterCompania, filterTipo, filterMes]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [actasRes, companiasRes] = await Promise.all([
                bomberosActaAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setActas(actasRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar actas');
        } finally {
            setLoading(false);
        }
    };

    const filterActas = () => {
        let filtered = [...actas];
        
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(a => 
                a.titulo?.toLowerCase().includes(search) ||
                a.lugar?.toLowerCase().includes(search) ||
                a.descripcion?.toLowerCase().includes(search)
            );
        }
        
        if (filterCompania !== 'all') {
            filtered = filtered.filter(a => a.compania_id === filterCompania);
        }
        
        if (filterTipo !== 'all') {
            filtered = filtered.filter(a => a.tipo === filterTipo);
        }
        
        if (filterMes !== 'all') {
            filtered = filtered.filter(a => a.fecha?.startsWith(filterMes));
        }
        
        // Sort by date descending
        filtered.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
        
        setFilteredActas(filtered);
    };

    const getCompaniaNombre = (companiaId) => {
        const comp = companias.find(c => c.id === companiaId);
        return comp?.nombre || 'N/A';
    };

    const getTipoConfig = (tipo) => {
        return TIPOS_ACTA[tipo] || TIPOS_ACTA.otro;
    };

    const getAsistenciaStats = (asistentes) => {
        if (!asistentes || asistentes.length === 0) return { total: 0, presentes: 0 };
        const presentes = asistentes.filter(a => a.presente).length;
        return { total: asistentes.length, presentes };
    };

    // Get unique months from actas
    const mesesDisponibles = [...new Set(actas.map(a => a.fecha?.substring(0, 7)).filter(Boolean))].sort().reverse();

    // Stats
    const stats = {
        total: filteredActas.length,
        entrenamientos: filteredActas.filter(a => a.tipo === 'entrenamiento').length,
        guardias: filteredActas.filter(a => a.tipo === 'guardia').length,
        emergencias: filteredActas.filter(a => a.tipo === 'emergencia').length,
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-64" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <FileText className="h-6 w-6 text-blue-500" />
                            Actas de Actividad
                        </h1>
                        <p className="text-muted-foreground">{stats.total} actas registradas</p>
                    </div>
                </div>
                {tienePermiso('registrar_asistencia') && (
                    <Button onClick={() => navigate('/bomberos/actas/nueva')} data-testid="nueva-acta-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nueva Acta
                    </Button>
                )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                                <FileText className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.total}</p>
                                <p className="text-xs text-muted-foreground">Total Actas</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100">
                                <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.entrenamientos}</p>
                                <p className="text-xs text-muted-foreground">Entrenamientos</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-100">
                                <Users className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.guardias}</p>
                                <p className="text-xs text-muted-foreground">Guardias</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-100">
                                <FileText className="h-5 w-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{stats.emergencias}</p>
                                <p className="text-xs text-muted-foreground">Emergencias</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        Filtros
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por título, lugar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={filterCompania} onValueChange={setFilterCompania}>
                            <SelectTrigger data-testid="filter-compania">
                                <SelectValue placeholder="Compañía" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas las compañías</SelectItem>
                                {companias.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterTipo} onValueChange={setFilterTipo}>
                            <SelectTrigger data-testid="filter-tipo">
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los tipos</SelectItem>
                                {Object.entries(TIPOS_ACTA).map(([key, config]) => (
                                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterMes} onValueChange={setFilterMes}>
                            <SelectTrigger data-testid="filter-mes">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los meses</SelectItem>
                                {mesesDisponibles.map(mes => (
                                    <SelectItem key={mes} value={mes}>{mes}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0">
                    {filteredActas.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No hay actas registradas</p>
                            {tienePermiso('registrar_asistencia') && (
                                <Button variant="outline" className="mt-4" onClick={() => navigate('/bomberos/actas/nueva')}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Crear Primera Acta
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Título</TableHead>
                                    <TableHead>Compañía</TableHead>
                                    <TableHead>Horario</TableHead>
                                    <TableHead>Lugar</TableHead>
                                    <TableHead>Asistencia</TableHead>
                                    <TableHead className="w-20">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredActas.map((acta) => {
                                    const tipoConfig = getTipoConfig(acta.tipo);
                                    const asistStats = getAsistenciaStats(acta.asistentes);
                                    
                                    return (
                                        <TableRow key={acta.id} data-testid={`acta-${acta.id}`}>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                                    {acta.fecha}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={tipoConfig.color}>
                                                    {tipoConfig.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="max-w-xs truncate">
                                                {acta.titulo}
                                            </TableCell>
                                            <TableCell>{getCompaniaNombre(acta.compania_id)}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-sm">
                                                    <Clock className="h-3 w-3" />
                                                    {acta.hora_inicio} - {acta.hora_fin}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-sm max-w-32 truncate">
                                                    <MapPin className="h-3 w-3 shrink-0" />
                                                    {acta.lugar || '-'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Users className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium">{asistStats.presentes}</span>
                                                    <span className="text-muted-foreground">/ {asistStats.total}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => navigate(`/bomberos/actas/${acta.id}`)}
                                                    data-testid={`ver-acta-${acta.id}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
        </>
    );
}
